import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  getDashboard,
  getWidgetData,
  listBoards,
  updateDashboard,
} from "./api/boardApi";
import { errorToast, successToast } from "../../redux/actionCreator";
import "./dashboards.scss";

// View a dashboard. Each widget renders via its own kind-specific component
// (NumberWidget, StatusDistWidget, PeopleLoadWidget) and lazy-loads its
// data through GET /v1/dashboards/:id/widgets/:wid/data.
const WIDGET_KINDS = [
  { kind: "number", label: "Number (total items)" },
  { kind: "status_dist", label: "Status distribution" },
  { kind: "people_load", label: "People load" },
  { kind: "people_status", label: "People × Status (per-user status counts)" },
];

export default function DashboardViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [boards, setBoards] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ kind: "number", title: "", board_ids: [], column_id: null, status_column_id: null });
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = async () => {
    try {
      const [r, b] = await Promise.all([getDashboard(id), listBoards({ limit: 500 })]);
      setD(r.data);
      setBoards(b.data || []);
    } catch (e) { errorToast(e.message); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <div className="dashboards-list-page"><Spinner size="sm" animation="border" /> Loading…</div>;

  const startAddWidget = () => {
    setEditing(null);
    // Auto-select if there's exactly one board — the most common case for
    // a single-project dashboard and a frequent source of "data is empty"
    // confusion (users don't realize the multi-select needs an explicit
    // click).
    const presetBoards = boards.length === 1 ? [String(boards[0]._id)] : [];
    setDraft({ kind: "number", title: "New widget", board_ids: presetBoards, column_id: null, status_column_id: null });
    setShowAdd(true);
  };
  const startEditWidget = (w) => {
    setEditing(w);
    setDraft({
      kind: w.kind,
      title: w.title,
      board_ids: (w.board_ids || []).map(String),
      column_id: w.column_id || null,
      status_column_id: w.settings?.status_column_id || null,
    });
    setShowAdd(true);
  };

  const saveWidget = async () => {
    if (!draft.title.trim() || !draft.board_ids.length) return;
    const next = [...(d.widgets || [])];
    const payload = {
      kind: draft.kind,
      title: draft.title.trim(),
      board_ids: draft.board_ids,
      column_id: draft.column_id || null,
      settings:
        draft.kind === "people_status"
          ? { status_column_id: draft.status_column_id || null }
          : {},
    };
    if (editing) {
      const idx = next.findIndex((w) => String(w._id) === String(editing._id));
      if (idx >= 0) next[idx] = { ...editing, ...payload };
    } else {
      next.push(payload);
    }
    try {
      const res = await updateDashboard(id, { widgets: next });
      setD(res.data);
      setShowAdd(false);
      successToast("Saved");
    } catch (e) { errorToast(e.message); }
  };

  const deleteWidget = async (w) => {
    if (!window.confirm(`Remove widget "${w.title}"?`)) return;
    const next = (d.widgets || []).filter((x) => String(x._id) !== String(w._id));
    try {
      const res = await updateDashboard(id, { widgets: next });
      setD(res.data);
    } catch (e) { errorToast(e.message); }
  };

  // Drag-to-reorder: drop widget A onto widget B → A lands right after B.
  // Persisted via updateDashboard so position survives reloads + sticks for
  // other viewers.
  const handleDragStart = (e, w) => {
    setDragId(String(w._id));
    e.dataTransfer.setData("text/plain", String(w._id));
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, w) => {
    if (!dragId || dragId === String(w._id)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(String(w._id));
  };
  const handleDrop = async (e, target) => {
    e.preventDefault();
    setDragOverId(null);
    const src = dragId;
    setDragId(null);
    if (!src || src === String(target._id)) return;
    const widgets = (d.widgets || []).slice();
    const fromIdx = widgets.findIndex((w) => String(w._id) === src);
    if (fromIdx < 0) return;
    const [moved] = widgets.splice(fromIdx, 1);
    const toIdx = widgets.findIndex((w) => String(w._id) === String(target._id));
    widgets.splice(toIdx + 1, 0, moved);

    // Optimistic local update, then persist.
    setD((cur) => ({ ...cur, widgets }));
    try {
      await updateDashboard(id, { widgets });
    } catch (err) {
      errorToast(err.message);
      // Roll back by refetching.
      const r = await getDashboard(id);
      setD(r.data);
    }
  };

  const statusColumnsOnSelectedBoards = boards
    .filter((b) => draft.board_ids.includes(String(b._id)))
    .flatMap((b) => (b.columns || []).filter((c) => c.type === "status").map((c) => ({ ...c, _board: b.name })));
  const peopleColumnsOnSelectedBoards = boards
    .filter((b) => draft.board_ids.includes(String(b._id)))
    .flatMap((b) => (b.columns || []).filter((c) => c.type === "people").map((c) => ({ ...c, _board: b.name })));

  return (
    <div className="dashboards-list-page">
      <header className="dashboards-head">
        <button className="back-btn" onClick={() => navigate("/dashboards")}>‹ Dashboards</button>
        <h2>{d.name}</h2>
        <Button variant="primary" onClick={startAddWidget}>+ Add widget</Button>
      </header>

      <div className="widget-grid">
        {(d.widgets || []).length === 0 && (
          <div className="empty-state">Add widgets to start visualizing your boards.</div>
        )}
        {(d.widgets || []).map((w) => (
          <WidgetCard
            key={w._id}
            dashboardId={id}
            widget={w}
            isDragging={dragId === String(w._id)}
            isOver={dragOverId === String(w._id)}
            onDragStart={(e) => handleDragStart(e, w)}
            onDragOver={(e) => handleDragOver(e, w)}
            onDragLeave={() => setDragOverId(null)}
            onDrop={(e) => handleDrop(e, w)}
            onDragEnd={() => { setDragId(null); setDragOverId(null); }}
            onEdit={() => startEditWidget(w)}
            onDelete={() => deleteWidget(w)}
          />
        ))}
      </div>

      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton><Modal.Title>{editing ? "Edit widget" : "New widget"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control autoFocus value={draft.title} onChange={(e) => setDraft((x) => ({ ...x, title: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Kind</Form.Label>
            <Form.Select value={draft.kind} onChange={(e) => setDraft((x) => ({ ...x, kind: e.target.value, column_id: null, status_column_id: null }))}>
              {WIDGET_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Source boards (Cmd/Ctrl+click for multi)</Form.Label>
            <Form.Select
              multiple
              value={draft.board_ids}
              onChange={(e) => setDraft((x) => ({ ...x, board_ids: Array.from(e.target.selectedOptions).map((o) => o.value) }))}
              style={{ height: 140 }}
            >
              {boards.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
            </Form.Select>
            {boards.length === 0 ? (
              <Form.Text className="text-danger">
                No boards available — create or get added to a board first.
              </Form.Text>
            ) : draft.board_ids.length === 0 ? (
              <Form.Text className="text-muted">
                Click a board to select it. Hold Cmd/Ctrl to pick more than one.
              </Form.Text>
            ) : (
              <Form.Text className="text-muted">
                {draft.board_ids.length} board{draft.board_ids.length === 1 ? "" : "s"} selected
              </Form.Text>
            )}
          </Form.Group>
          {draft.kind === "status_dist" && (
            <Form.Group className="mb-3">
              <Form.Label>Status column</Form.Label>
              <Form.Select
                value={draft.column_id || ""}
                onChange={(e) => setDraft((x) => ({ ...x, column_id: e.target.value }))}
                disabled={statusColumnsOnSelectedBoards.length === 0}
              >
                <option value="">— pick a column —</option>
                {statusColumnsOnSelectedBoards.map((c) => (
                  <option key={c._id} value={c._id}>{c._board} → {c.title}</option>
                ))}
              </Form.Select>
              {draft.board_ids.length === 0 ? (
                <Form.Text className="text-muted">Select a source board above to list its status columns.</Form.Text>
              ) : statusColumnsOnSelectedBoards.length === 0 ? (
                <Form.Text className="text-warning">
                  The selected board{draft.board_ids.length > 1 ? "s have" : " has"} no Status-type columns.
                  Add one on the board first (Add column → Status), then re-open this dialog.
                </Form.Text>
              ) : null}
            </Form.Group>
          )}
          {draft.kind === "people_load" && (
            <Form.Group className="mb-3">
              <Form.Label>People column</Form.Label>
              <Form.Select
                value={draft.column_id || ""}
                onChange={(e) => setDraft((x) => ({ ...x, column_id: e.target.value }))}
                disabled={peopleColumnsOnSelectedBoards.length === 0}
              >
                <option value="">— pick a column —</option>
                {peopleColumnsOnSelectedBoards.map((c) => (
                  <option key={c._id} value={c._id}>{c._board} → {c.title}</option>
                ))}
              </Form.Select>
              {draft.board_ids.length === 0 ? (
                <Form.Text className="text-muted">Select a source board above to list its people columns.</Form.Text>
              ) : peopleColumnsOnSelectedBoards.length === 0 ? (
                <Form.Text className="text-warning">
                  The selected board{draft.board_ids.length > 1 ? "s have" : " has"} no People-type columns.
                  Add one on the board first (Add column → People), then re-open this dialog.
                </Form.Text>
              ) : null}
            </Form.Group>
          )}
          {draft.kind === "people_status" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>People column</Form.Label>
                <Form.Select
                  value={draft.column_id || ""}
                  onChange={(e) => setDraft((x) => ({ ...x, column_id: e.target.value }))}
                  disabled={peopleColumnsOnSelectedBoards.length === 0}
                >
                  <option value="">— pick a column —</option>
                  {peopleColumnsOnSelectedBoards.map((c) => (
                    <option key={c._id} value={c._id}>{c._board} → {c.title}</option>
                  ))}
                </Form.Select>
                {draft.board_ids.length === 0 ? (
                  <Form.Text className="text-muted">Select a source board above to list its people columns.</Form.Text>
                ) : peopleColumnsOnSelectedBoards.length === 0 ? (
                  <Form.Text className="text-warning">
                    The selected board{draft.board_ids.length > 1 ? "s have" : " has"} no People-type columns.
                  </Form.Text>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status column</Form.Label>
                <Form.Select
                  value={draft.status_column_id || ""}
                  onChange={(e) => setDraft((x) => ({ ...x, status_column_id: e.target.value }))}
                  disabled={statusColumnsOnSelectedBoards.length === 0}
                >
                  <option value="">— pick a column —</option>
                  {statusColumnsOnSelectedBoards.map((c) => (
                    <option key={c._id} value={c._id}>{c._board} → {c.title}</option>
                  ))}
                </Form.Select>
                {draft.board_ids.length === 0 ? (
                  <Form.Text className="text-muted">Select a source board above to list its status columns.</Form.Text>
                ) : statusColumnsOnSelectedBoards.length === 0 ? (
                  <Form.Text className="text-warning">
                    The selected board{draft.board_ids.length > 1 ? "s have" : " has"} no Status-type columns.
                  </Form.Text>
                ) : null}
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={saveWidget}
            disabled={
              !draft.title.trim() ||
              !draft.board_ids.length ||
              ((draft.kind === "status_dist" || draft.kind === "people_load") && !draft.column_id) ||
              (draft.kind === "people_status" && (!draft.column_id || !draft.status_column_id))
            }
          >
            {editing ? "Save" : "Add"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function WidgetCard({
  dashboardId, widget, onEdit, onDelete,
  isDragging, isOver, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const boardIdsKey = JSON.stringify(widget.board_ids);
  const statusColId = widget.settings?.status_column_id;
  useEffect(() => {
    setData(null);
    setErr(null);
    getWidgetData(dashboardId, widget._id)
      .then((res) => setData(res.data))
      .catch((e) => setErr(e.message));
  }, [dashboardId, widget._id, widget.kind, widget.column_id, statusColId, boardIdsKey]);

  return (
    <div
      className={`widget-card ${widget.kind === "people_status" ? "wide" : ""} ${isDragging ? "dragging" : ""} ${isOver ? "drag-over" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <header className="widget-card-head">
        <span className="widget-grip" title="Drag to reorder">⠿</span>
        <h4>{widget.title}</h4>
        <div className="widget-actions">
          <button className="linkish" onClick={onEdit}>Edit</button>
          <button className="linkish del" onClick={onDelete}>Remove</button>
        </div>
      </header>
      <div className="widget-body">
        {err && <div className="widget-error">{err}</div>}
        {!data && !err && <div className="muted">Loading…</div>}
        {data && widget.kind === "number" && <NumberWidget data={data} />}
        {data && widget.kind === "status_dist" && <StatusDistWidget data={data} />}
        {data && widget.kind === "people_load" && <PeopleLoadWidget data={data} />}
        {data && widget.kind === "people_status" && <PeopleStatusWidget data={data} />}
      </div>
    </div>
  );
}

function NumberWidget({ data }) {
  return (
    <div className="widget-number">
      <div className="widget-number-value">{data.count ?? 0}</div>
      <div className="widget-number-label">items</div>
    </div>
  );
}

function StatusDistWidget({ data }) {
  const max = Math.max(1, ...data.buckets.map((b) => b.count));
  return (
    <div className="widget-bars">
      {data.buckets.map((b) => (
        <div key={b.id} className="widget-bar-row">
          <span className="bar-label">{b.label}</span>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${(b.count / max) * 100}%`, background: b.color }} />
          </div>
          <span className="bar-count">{b.count}</span>
        </div>
      ))}
    </div>
  );
}

function PeopleLoadWidget({ data }) {
  return (
    <div className="widget-list">
      {data.rows.length === 0 && <div className="muted">No assignments yet.</div>}
      {data.rows.map((r) => {
        const label = r.name || `User ${String(r.user_id).slice(-6)}`;
        const initials = (r.name || "?")
          .split(/\s+/)
          .map((p) => p[0])
          .filter(Boolean)
          .slice(0, 2)
          .join("")
          .toUpperCase() || "?";
        return (
          <div key={r.user_id} className="widget-list-row">
            <span className="widget-list-avatar" title={r.user_id}>{initials}</span>
            <span className="widget-list-name">{label}</span>
            <span className="widget-list-count">{r.count}</span>
          </div>
        );
      })}
    </div>
  );
}

// Matrix: one row per assignee, one column per status label, cells = counts.
// Cells with zero are dimmed; the trailing "Total" column sums the row.
function PeopleStatusWidget({ data }) {
  const statuses = data.statuses || [];
  const rows = data.rows || [];
  if (!statuses.length) {
    return <div className="muted">Pick a People column and a Status column.</div>;
  }
  if (!rows.length) {
    return <div className="muted">No assignments yet.</div>;
  }
  return (
    <div className="widget-matrix-scroll">
      <table className="widget-matrix">
        <thead>
          <tr>
            <th className="wm-user">Person</th>
            {statuses.map((s) => (
              <th key={s.id} title={s.label}>
                <span className="wm-dot" style={{ background: s.color }} />
                {s.label}
              </th>
            ))}
            <th className="wm-total">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const label = r.name || `User ${String(r.user_id).slice(-6)}`;
            const initials =
              (r.name || "?")
                .split(/\s+/)
                .map((p) => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join("")
                .toUpperCase() || "?";
            return (
              <tr key={r.user_id}>
                <td className="wm-user">
                  <span className="wm-avatar" title={r.user_id}>{initials}</span>
                  <span className="wm-name" title={label}>{label}</span>
                </td>
                {statuses.map((s) => {
                  const n = r.counts?.[s.id] || 0;
                  return (
                    <td key={s.id} className={n ? "wm-cell" : "wm-cell wm-zero"}>
                      {n || "·"}
                    </td>
                  );
                })}
                <td className="wm-total">{r.total}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
